import QuoteHelp from './QuoteHelp';
import React from 'react';
import QuoteTaxBreakdown from './QuoteTaxBreakdown';
import DeleteIcon from '@mui/icons-material/Delete';
import Input from '../../../shared/components/Input/Input';
import Select from '../../../shared/components/Select/Select';
import Button from '../../../shared/components/Button/Button';
import Checkbox from '../../../shared/components/Checkbox/Checkbox';
import IconButton from '../../../shared/components/IconButton/IconButton';
import { quoteUnits } from '../quoteUiOptions';
import QuoteDecimalInput from './QuoteDecimalInput';

const OptionDecimalInput = ({ label, ...props }) => (
  <label className="quote-option-line__field">
    <span>{label}</span>
    <QuoteDecimalInput {...props} ariaLabel={label} inputComponent={Input} size="small" />
  </label>
);

const optionLines = (option) => (option.sections || []).flatMap((section) => section.lines || []);

const findCalculatedOption = (option, calculated) => {
  const viewRows = calculated?.optionViews || [];
  return viewRows.find((row) => String(row.id) === String(option.id)) || null;
};

const QuoteOptionsPanel = ({
  editor,
  calculated,
  formatMoney = (value) => `${Number(value || 0).toFixed(2)} €`,
  onAddOption,
  onUpdateOption,
  onRemoveOption,
  onClearOptions,
  onAddOptionLine,
  onUpdateOptionLine,
  onRemoveOptionLine,
}) => (
  <section className={`quote-panel quote-options-panel ${!editor.permissions?.canEdit ? 'quote-panel--readonly' : ''}`}>
    <header className="quote-options-panel__header">
      <div>
        <h2>Options</h2>
        <QuoteHelp title="Calcul des options">Chaque option possède son propre calcul et reste exclue du devis initial, même sélectionnée. Le total combiné est indicatif.</QuoteHelp>
      </div>
      <Button variant="primary" disabled={!editor.permissions?.canEdit} onClick={onAddOption}>Ajouter une option</Button>
      {onClearOptions && <Button variant="secondary" disabled={!editor.permissions?.canEdit} onClick={onClearOptions}>Vider les options</Button>}
    </header>

    <div className="quote-options-list">
      {(editor.options || []).map((option) => {
        const result = findCalculatedOption(option, calculated);
        const totals = result?.totals || result || {};
        return (
          <article className="quote-option-editor" key={option.id}>
            <header className="quote-option-editor__header">
              <Input className="quote-option-editor__title" size="small" label="Nom de l’option" disabled={!editor.permissions?.canEdit} value={option.title || ''} onChange={(event) => onUpdateOption(option.id, 'title', event.target.value)} />
              <Checkbox className="quote-option-editor__selected" label="Inclure dans la comparaison indicative" disabled={!editor.permissions?.canEdit} checked={Boolean(option.selected)} onChange={(event) => onUpdateOption(option.id, 'selected', event.target.checked)} />
              <Button className="quote-option-editor__delete" size="small" variant="danger" disabled={!editor.permissions?.canEdit} onClick={() => onRemoveOption(option.id)}>Supprimer</Button>
            </header>

            <div className="quote-option-lines">
              {optionLines(option).map((line) => (
                <article className="quote-option-line" key={line.id}>
                  <Input className="quote-option-line__description" size="small" label="Désignation" disabled={!editor.permissions?.canEdit} value={line.description || ''} onChange={(event) => onUpdateOptionLine(option.id, line.id, 'description', event.target.value)} />
                  <OptionDecimalInput label="Quantité" min={0} disabled={!editor.permissions?.canEdit} value={line.quantity ?? 1} onChange={(value) => onUpdateOptionLine(option.id, line.id, 'quantity', value)} />
                  <Select size="small" label="Unité" disabled={!editor.permissions?.canEdit} value={quoteUnits.includes(line.unit) ? line.unit : 'Ens'} onChange={(event) => onUpdateOptionLine(option.id, line.id, 'unit', event.target.value)} options={quoteUnits.map((unit) => ({ value: unit, label: unit }))} />
                  <OptionDecimalInput label="Heures / unité" min={0} disabled={!editor.permissions?.canEdit} value={line.estimatedHours ?? 0} onChange={(value) => onUpdateOptionLine(option.id, line.id, 'estimatedHours', value)} />
                  <OptionDecimalInput label="Matériel / unité" min={0} disabled={!editor.permissions?.canEdit} value={line.purchaseCost ?? 0} onChange={(value) => onUpdateOptionLine(option.id, line.id, 'purchaseCost', value)} />
                  <OptionDecimalInput label="Sous-traitance / unité" min={0} disabled={!editor.permissions?.canEdit} value={line.subcontractingCost ?? 0} onChange={(value) => onUpdateOptionLine(option.id, line.id, 'subcontractingCost', value)} />
                  <label className="quote-option-line__field"><span>TVA</span><div className="quote-option-line__suffix"><QuoteDecimalInput min={0} disabled={!editor.permissions?.canEdit} value={line.vatRate ?? editor.defaultVatRate ?? 0} onChange={(value) => onUpdateOptionLine(option.id, line.id, 'vatRate', value)} ariaLabel="TVA" inputComponent={Input} size="small" /><span>%</span></div></label>
                  <IconButton className="quote-option-line__remove" size="small" variant="danger" tooltip="Retirer cette ligne" disabled={!editor.permissions?.canEdit} onClick={() => onRemoveOptionLine(option.id, line.id)}><DeleteIcon fontSize="small" /></IconButton>
                </article>
              ))}
            </div>

            <footer className="quote-option-editor__footer">
              <Button size="small" variant="secondary" disabled={!editor.permissions?.canEdit} onClick={() => onAddOptionLine(option.id)}>Ajouter une ligne</Button>
              {result && (
                <dl className="quote-option-editor__totals" aria-label={`Totaux ${option.title || 'option'}`}>
                  {(result.cost_price ?? totals.costPrice) != null && <div><dt>PR</dt><dd>{formatMoney(result.cost_price ?? totals.costPrice)}</dd></div>}
                  <div><dt>HT</dt><dd>{formatMoney(result.net_selling_price_ht ?? totals.totalHT ?? totals.netSellingPriceHT ?? totals.net_selling_price_ht ?? 0)}</dd></div>
                  <div><dt>TVA</dt><dd>{formatMoney(result.tax ?? totals.totalVat ?? totals.tax ?? 0)}</dd></div>
                  <div className="total"><dt>TTC</dt><dd>{formatMoney(result.total_ttc ?? totals.totalTTC ?? totals.total_ttc ?? 0)}</dd></div>
                </dl>
              )}
            </footer>
            {result && <QuoteTaxBreakdown source={result} formatMoney={formatMoney} />}
            {!result && calculated?.source === 'recorded' && <p>Totaux de cette option non disponibles dans la version enregistrée.</p>}
          </article>
        );
      })}
    </div>
    {!(editor.options || []).length && <p className="quote-detail-empty">Aucune option sur cette version.</p>}
  </section>
);

export default QuoteOptionsPanel;
